<template>
    <div class="sap-orders-wrapper">
        <div>
            <div class="row gutters mb-0">
                <div class="col-sm-12">
                    <div class="card mb-0">
                        <div class="card-body">
                            <div class="row">
                                <div class="col-sm-4">
                                    <div class="field-wrapper">
                                        <div class="field-placeholder">From Date *</div>
                                        <input
                                            class="form-control"
                                            v-model="sapMeta.from_date"
                                            :class="dateFieldClass('from_date')"
                                            type="date"
                                        />
                                        <span v-if="errors.from_date" class="invalid-feedback"
                                            >{{ errorText('from_date') }}</span
                                        >
                                    </div>
                                </div>
                                <div class="col-sm-4">
                                    <div class="field-wrapper">
                                        <div class="field-placeholder">To Date *</div>
                                        <input
                                            class="form-control"
                                            v-model="sapMeta.to_date"
                                            :class="dateFieldClass('to_date')"
                                            type="date"
                                        />
                                        <span v-if="errors.to_date" class="invalid-feedback"
                                            >{{ errorText('to_date') }}</span
                                        >
                                    </div>
                                </div>
                                <div class="col-sm-4 mt-2">
                                    <button type="button" class="btn btn-primary ms-1" @click="generateSAPOrders">
                                        Generate
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="sap-orders-content">
            <div class="row gutters mt-3" v-if="overallData">
                <div class="col-sm-12">
                    <h5>SAP Orders Summary : Overall</h5>
                    <div class="dashboard-header">
                        <div class="dashboard-header-content">
                            <div class="sales-tile-container">
                                <div
                                    class="sales-tile"
                                    :class="{
                                        'sap-tile-selected': isOverallSummarySelected('received'),
                                        'sap-tile-default': !hasSapTileData(overallData.total_orders),
                                    }"
                                    :style="getSapTileStyle(overallData.total_orders, isOverallSummarySelected('received'))"
                                    @click="openOverallSummaryList('received')"
                                >
                                    <h4 id="label">{{ displayCount(overallData.total_orders) }}</h4>
                                    <h6>Received Orders</h6>
                                </div>
                                <div
                                    class="sales-tile"
                                    :class="{
                                        'sap-tile-selected': isOverallSummarySelected('completed'),
                                        'sap-tile-default': !hasSapTileData(overallData.field_orders),
                                    }"
                                    :style="getSapTileStyle(overallData.field_orders, isOverallSummarySelected('completed'))"
                                    @click="openOverallSummaryList('completed')"
                                >
                                    <h4 id="label">{{ displayCount(overallData.field_orders) }}</h4>
                                    <h6>Completed Orders</h6>
                                </div>
                                <div
                                    class="sales-tile sap-deviation-tile"
                                    :class="{
                                        'sap-tile-selected': isOverallSummarySelected('deviation'),
                                        'sap-tile-default': !hasSapTileData(overallData.deviation),
                                    }"
                                    :style="getSapDeviationStyle(overallData.deviation, isOverallSummarySelected('deviation'))"
                                    @click="openOverallSummaryList('deviation')"
                                >
                                    <h4 id="label">{{ displayCount(overallData.deviation) }}</h4>
                                    <h6>Deviations</h6>
                                </div>
                                <div class="sap-live-pending-gap"></div>
                                <div
                                    class="sales-tile sap-live-pending-tile"
                                    :class="{
                                        'sap-tile-selected': isOverallSummarySelected('pending'),
                                        'sap-tile-default': !hasSapTileData(overallData.livePendingOrders),
                                    }"
                                    :style="getSapLivePendingStyle(overallData.livePendingOrders, isOverallSummarySelected('pending'))"
                                    @click="openOverallSummaryList('pending')"
                                >
                                    <h4 id="label">{{ displayCount(overallData.livePendingOrders) }}</h4>
                                    <h6>Live Pending Orders</h6>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row gutters mt-3" v-if="plants.length != 0">
                <div class="col-sm-12">
                    <h5>Plants</h5>
                    <div class="dashboard-header">
                        <div class="dashboard-header-content">
                            <div class="sales-tile-container">
                                <template v-for="plant in plants" :key="plant.plant_id">
                                    <div
                                        class="sales-tile"
                                        :class="{
                                            'sap-tile-selected': isPlantSelected(plant),
                                            'sap-tile-default': !hasSapTileData(plant.total_orders),
                                        }"
                                        :style="getSapTileStyle(plant.total_orders, isPlantSelected(plant))"
                                        @click="selectPlant(plant)"
                                    >
                                        <h4 id="label">{{ displayCount(plant.total_orders) }}</h4>
                                        <h6>{{ plant.plant_code }}</h6>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row gutters mt-3" v-if="departments.length != 0">
                <div class="col-sm-12">
                    <h5>Departments : {{ plant_code }}</h5>
                    <div class="dashboard-header">
                        <div class="dashboard-header-content">
                            <div class="sales-tile-container">
                                <template v-for="department in departments" :key="department.department_id">
                                    <div
                                        class="sales-tile"
                                        :class="{
                                            'sap-tile-selected': isDepartmentSelected(department),
                                            'sap-tile-default': !hasSapTileData(department.total_orders),
                                        }"
                                        :style="getSapTileStyle(department.total_orders, isDepartmentSelected(department))"
                                        @click="selectDepartment(department)"
                                    >
                                        <h4 id="label">{{ displayCount(department.total_orders) }}</h4>
                                        <h6>{{ department.department_name }}</h6>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row gutters mt-3" v-if="plannerGroups.length != 0">
                <div class="col-sm-12">
                    <h5>Planner Groups : {{ plant_code }} : {{ department_name }}</h5>
                    <div class="dashboard-header">
                        <div class="dashboard-header-content">
                            <div class="sales-tile-container">
                                <template v-for="plannerGroup in plannerGroups" :key="plannerGroup.planner_group">
                                    <div
                                        class="sales-tile"
                                        :class="{
                                            'sap-tile-selected': isPlannerGroupSelected(plannerGroup),
                                            'sap-tile-default': !hasSapTileData(plannerGroup.total_orders),
                                        }"
                                        :style="getSapTileStyle(plannerGroup.total_orders, isPlannerGroupSelected(plannerGroup))"
                                        @click="selectPlannerGroup(plannerGroup)"
                                    >
                                        <h4 id="label">{{ displayCount(plannerGroup.total_orders) }}</h4>
                                        <h6>{{ plannerGroup.planner_group }}</h6>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row gutters mt-3" v-if="sapOrdersData">
                <div class="col-sm-12" v-if="selectedPlannerGroup">
                    <h5>SAP Orders Summary : {{ plant_code }} : {{ department_name }} : {{ selectedPlannerGroup }}</h5>
                    <div class="dashboard-header">
                        <div class="dashboard-header-content">
                            <div class="sales-tile-container">
                                <div
                                    class="sales-tile"
                                    :class="{
                                        'sap-tile-selected': isDetailSummarySelected('received'),
                                        'sap-tile-default': !hasSapTileData(sapOrdersData.total_orders),
                                    }"
                                    :style="getSapTileStyle(sapOrdersData.total_orders, isDetailSummarySelected('received'))"
                                    @click="openDetailSummaryList('received')"
                                    style="cursor: pointer"
                                >
                                    <h4 id="label">{{ displayCount(sapOrdersData.total_orders) }}</h4>
                                    <h6>Received Orders</h6>
                                </div>
                                <div
                                    class="sales-tile"
                                    :class="{
                                        'sap-tile-selected': isDetailSummarySelected('completed'),
                                        'sap-tile-default': !hasSapTileData(sapOrdersData.field_orders),
                                    }"
                                    :style="getSapTileStyle(sapOrdersData.field_orders, isDetailSummarySelected('completed'))"
                                    @click="openDetailSummaryList('completed')"
                                    style="cursor: pointer"
                                >
                                    <h4 id="label">{{ displayCount(sapOrdersData.field_orders) }}</h4>
                                    <h6>Completed Orders</h6>
                                </div>
                                <div
                                    class="sales-tile sap-deviation-tile"
                                    :class="{
                                        'sap-tile-selected': isDetailSummarySelected('deviation'),
                                        'sap-tile-default': !hasSapTileData(sapOrdersData.deviation),
                                    }"
                                    :style="getSapDeviationStyle(sapOrdersData.deviation, isDetailSummarySelected('deviation'))"
                                    @click="openDetailSummaryList('deviation')"
                                    style="cursor: pointer"
                                >
                                    <h4 id="label">{{ displayCount(sapOrdersData.deviation) }}</h4>
                                    <h6>Deviations</h6>
                                </div>
                                <div class="sap-live-pending-gap"></div>
                                <div
                                    class="sales-tile sap-live-pending-tile"
                                    :class="{
                                        'sap-tile-selected': isDetailSummarySelected('pending'),
                                        'sap-tile-default': !hasSapTileData(sapOrdersData.livePendingOrders),
                                    }"
                                    :style="getSapLivePendingStyle(sapOrdersData.livePendingOrders, isDetailSummarySelected('pending'))"
                                    @click="openDetailSummaryList('pending')"
                                    style="cursor: pointer"
                                >
                                    <h4 id="label">{{ displayCount(sapOrdersData.livePendingOrders) }}</h4>
                                    <h6>Live Pending Orders</h6>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Summary List Table -->
            <div class="row gutters mt-3" v-if="summaryListType">
                <div class="col-sm-12">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <h5 class="mb-0">{{ summaryListHeading }}</h5>
                        <div class="d-flex align-items-center">
                            <button
                                type="button"
                                class="sap-excel-download-btn me-2"
                                title="Download Excel"
                                @click="downloadSummaryExcel"
                            >
                                Download <i class="fas fa-file-excel"></i>
                            </button>
                            <div class="input-group" style="max-width: 360px">
                                <input
                                    class="form-control"
                                    type="text"
                                    placeholder="Type here and press enter to search"
                                    v-model="summaryMeta.search"
                                    @keyup.enter="searchSummaryList"
                                />
                                <span class="input-group-text" @click="searchSummaryList" style="cursor: pointer">
                                    <i class="fas fa-search"></i>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="card">
                        <div class="card-body p-0">
                            <div class="table-responsive tabletop" style="max-height: none">
                                <table class="table table-sm table-bordered table-striped mb-0">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Order Number</th>
                                            <th>Order Description</th>
                                            <th>Equipment Number</th>
                                            <th>Equipment Description</th>
                                            <th>Short Text Order</th>
                                            <th>lower_limit</th>
                                            <th>upper_limit</th>
                                            <th>Inspection Result</th>
                                            <th>Remarks</th>
                                            <th>Uploaded By</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="summaryRows.length == 0">
                                            <td colspan="11" class="text-center">No records found</td>
                                        </tr>
                                        <template
                                            v-for="(equipment, key) in summaryRows"
                                            :key="equipment.order_equipment_id"
                                        >
                                            <tr class="equipment-row">
                                                <td>{{ summaryMeta.from + key }}</td>
                                                <td>{{ displayValue(equipment.order_number) }}</td>
                                                <td>{{ displayValue(equipment.order_desc) }}</td>
                                                <td>{{ displayValue(equipment.equipment_number) }}</td>
                                                <td>{{ displayValue(equipment.equipment_desc) }}</td>
                                                <td>{{ displayValue(equipment.short_text_order) }}</td>
                                                <td>{{ displayValue(equipment.lower_limit) }}</td>
                                                <td>{{ displayValue(equipment.upper_limit) }}</td>
                                                <td>{{ displayValue(equipment.inspection_result) }}</td>
                                                <td></td>
                                                <td></td>
                                            </tr>
                                            <tr
                                                v-for="(check, checkIndex) in getEquipmentChecks(equipment)"
                                                :key="check.order_check_id"
                                                class="check-row"
                                            >
                                                <td>{{ summaryMeta.from + key }}.{{ checkIndex + 1 }}</td>
                                                <td>{{ displayValue(check.order_number) }}</td>
                                                <td>{{ displayValue(check.order_desc) }}</td>
                                                <td>{{ displayValue(check.equipment_number) }}</td>
                                                <td>{{ displayValue(check.equipment_desc) }}</td>
                                                <td>{{ displayValue(check.short_text_order) }}</td>
                                                <td>{{ displayValue(check.lower_limit) }}</td>
                                                <td>{{ displayValue(check.upper_limit) }}</td>
                                                <td>{{ displayValue(check.inspection_result) }}</td>
                                                <td>{{ displayValue(check.remarks) }}</td>
                                                <td>{{ getCheckUserName(check) }}</td>
                                            </tr>
                                        </template>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div class="card-footer">
                            <div class="row align-items-center">
                                <div class="col-sm-2">
                                    <select
                                        class="form-control"
                                        v-model="summaryMeta.per_page"
                                        @change="onSummaryPerPageChange"
                                    >
                                        <option>10</option>
                                        <option>15</option>
                                        <option>20</option>
                                        <option>25</option>
                                        <option>30</option>
                                    </select>
                                </div>
                                <div class="col-sm-10">
                                    <Pagination
                                        class="pagination pagination-sm mb-0"
                                        :maxPage="summaryMeta.maxPage"
                                        :totalPages="summaryMeta.lastPage"
                                        :currentPage="summaryMeta.page"
                                        @pagechanged="onSummaryPageChange"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from "moment";
    import axios from "axios";
    import Pagination from "@/components/Pagination.vue";

    export default {
        name: "SAPOrdersTab",
        components: { Pagination },
        data() {
            let userId = "";
            if (this.$store.getters.user) {
                userId = this.$store.getters.user.user_id;
            }
            return {
                sapMeta: {
                    user_id: userId,
                    plant_id: "",
                    department_id: "",
                    planner_group: "",
                    from_date: new Date(Date.now() - 86400000).toISOString().split("T")[0],
                    to_date: new Date(Date.now() - 86400000).toISOString().split("T")[0],
                },
                sapOrdersData: null,
                overallData: null,
                plants: [],
                departments: [],
                plannerGroups: [],
                selectedPlantId: "",
                selectedDepartmentId: "",
                selectedPlannerGroup: "",
                plant_code: "",
                department_name: "",
                summaryListType: "",
                summaryScope: "",
                summaryTitle: "",
                summaryRows: [],
                summaryMeta: {
                    page: 1,
                    per_page: 10,
                    total: 0,
                    lastPage: 1,
                    from: 1,
                    maxPage: 1,
                    search: "",
                },
                errors: {},
            };
        },
        computed: {
            summaryListHeading() {
                let title = this.summaryTitle;
                let total = this.displayCount(this.summaryMeta.total);
                if (this.summaryScope == "overall") {
                    return title + " (" + total + ") : Overall";
                }
                return title + " (" + total + ") : " + this.plant_code + " : " + this.department_name + " : " + this.selectedPlannerGroup;
            },
        },
        mounted() {
            // Auto-load data when component is mounted (tab is clicked)
            this.generateSAPOrders();
        },
        methods: {
            generateSAPOrders() {
                let vm = this;
                vm.errors = {};

                if (!vm.sapMeta.from_date) {
                    vm.errors.from_date = ["From Date is required."];
                    return;
                }

                if (!vm.sapMeta.to_date) {
                    vm.errors.to_date = ["To Date is required."];
                    return;
                }

                if (new Date(vm.sapMeta.from_date) > new Date(vm.sapMeta.to_date)) {
                    vm.$store.dispatch("error", "From Date cannot be greater than To Date.");
                    return;
                }

                vm.selectedPlantId = "";
                vm.selectedDepartmentId = "";
                vm.selectedPlannerGroup = "";
                vm.plant_code = "";
                vm.department_name = "";
                vm.sapMeta.plant_id = "";
                vm.sapMeta.department_id = "";
                vm.sapMeta.planner_group = "";
                vm.sapOrdersData = null;
                vm.overallData = null;
                vm.departments = [];
                vm.plannerGroups = [];
                vm.resetSummaryList();

                let loader = vm.$loading.show();
                vm.$store
                    .dispatch("post", { uri: "getSAPOrders", data: vm.sapMeta })
                    .then(function (response) {
                        loader.hide();
                        if (response.data.data) {
                            vm.overallData = {
                                total_orders: response.data.data.total_orders,
                                field_orders: response.data.data.field_orders,
                                deviation: response.data.data.deviation,
                                livePendingOrders: response.data.data.livePendingOrders,
                            };
                            if (response.data.data.plants) {
                                vm.plants = response.data.data.plants;
                            } else {
                                vm.plants = [];
                            }
                        } else {
                            vm.overallData = null;
                            vm.plants = [];
                        }
                    })
                    .catch(function (error) {
                        loader.hide();
                        vm.overallData = null;
                        vm.plants = [];
                        if (error.response && error.response.data && error.response.data.errors) {
                            vm.errors = error.response.data.errors;
                        } else {
                            vm.errors = {};
                        }
                        let message = "An error occurred while fetching SAP Orders.";
                        if (error.response && error.response.data && error.response.data.message) {
                            message = error.response.data.message;
                        }
                        vm.$store.dispatch("error", message);
                    });
            },
            selectPlant(plant) {
                let vm = this;
                vm.selectedPlantId = plant.plant_id;
                vm.plant_code = plant.plant_code;
                vm.sapMeta.plant_id = plant.plant_id;
                vm.selectedDepartmentId = "";
                vm.selectedPlannerGroup = "";
                vm.department_name = "";
                vm.sapMeta.department_id = "";
                vm.sapMeta.planner_group = "";
                vm.sapOrdersData = null;
                vm.plannerGroups = [];
                vm.resetSummaryList();

                let loader = vm.$loading.show();
                vm.$store
                    .dispatch("post", {
                        uri: "getSAPOrderDepartments",
                        data: {
                            plant_id: vm.sapMeta.plant_id,
                            from_date: vm.sapMeta.from_date,
                            to_date: vm.sapMeta.to_date,
                            user_id: vm.sapMeta.user_id,
                        },
                    })
                    .then(function (response) {
                        loader.hide();
                        if (response.data) {
                            vm.departments = response.data;
                        } else {
                            vm.departments = [];
                        }
                    })
                    .catch(function (error) {
                        loader.hide();
                        vm.departments = [];
                        let message = "An error occurred while fetching departments.";
                        if (error.response && error.response.data && error.response.data.message) {
                            message = error.response.data.message;
                        }
                        vm.$store.dispatch("error", message);
                    });
            },
            selectDepartment(department) {
                let vm = this;
                vm.selectedDepartmentId = department.department_id;
                vm.department_name = department.department_name;
                vm.sapMeta.department_id = department.department_id;
                vm.selectedPlannerGroup = "";
                vm.sapMeta.planner_group = "";
                vm.sapOrdersData = null;
                vm.resetSummaryList();

                let loader = vm.$loading.show();
                vm.$store
                    .dispatch("post", {
                        uri: "getSAPOrderPlannerGroups",
                        data: {
                            plant_id: vm.sapMeta.plant_id,
                            department_id: vm.sapMeta.department_id,
                            from_date: vm.sapMeta.from_date,
                            to_date: vm.sapMeta.to_date,
                            user_id: vm.sapMeta.user_id,
                        },
                    })
                    .then(function (response) {
                        loader.hide();
                        if (response.data) {
                            vm.plannerGroups = vm.getUniquePlannerGroups(response.data);
                        } else {
                            vm.plannerGroups = [];
                        }
                    })
                    .catch(function (error) {
                        loader.hide();
                        vm.plannerGroups = [];
                        let message = "An error occurred while fetching planner groups.";
                        if (error.response && error.response.data && error.response.data.message) {
                            message = error.response.data.message;
                        }
                        vm.$store.dispatch("error", message);
                    });
            },
            selectPlannerGroup(plannerGroup) {
                this.selectedPlannerGroup = plannerGroup.planner_group;
                this.sapMeta.planner_group = plannerGroup.planner_group;
                this.sapOrdersData = {
                    total_orders: plannerGroup.total_orders,
                    field_orders: plannerGroup.field_orders,
                    deviation: plannerGroup.deviation,
                    livePendingOrders: plannerGroup.livePendingOrders,
                };
                this.resetSummaryList();
            },
            getUniquePlannerGroups(groups) {
                let uniqueGroups = [];
                let seen = {};
                if (!groups) {
                    return uniqueGroups;
                }
                for (let i = 0; i < groups.length; i++) {
                    let group = groups[i];
                    if (!group) {
                        continue;
                    }
                    let name = "";
                    if (group.planner_group) {
                        name = String(group.planner_group).trim().toUpperCase();
                    }
                    if (name == "") {
                        continue;
                    }
                    if (seen[name]) {
                        continue;
                    }
                    seen[name] = true;
                    uniqueGroups.push(group);
                }
                return uniqueGroups;
            },
            resetSummaryList() {
                this.summaryListType = "";
                this.summaryScope = "";
                this.summaryTitle = "";
                this.summaryRows = [];
                this.summaryMeta.page = 1;
                this.summaryMeta.total = 0;
                this.summaryMeta.lastPage = 1;
                this.summaryMeta.from = 1;
                this.summaryMeta.maxPage = 1;
                this.summaryMeta.search = "";
            },
            openOverallSummaryList(listType) {
                let vm = this;
                vm.selectedPlantId = "";
                vm.selectedDepartmentId = "";
                vm.selectedPlannerGroup = "";
                vm.plant_code = "";
                vm.department_name = "";
                vm.sapMeta.plant_id = "";
                vm.sapMeta.department_id = "";
                vm.sapMeta.planner_group = "";
                vm.sapOrdersData = null;
                vm.departments = [];
                vm.plannerGroups = [];
                vm.summaryScope = "overall";
                vm.summaryListType = listType;
                vm.summaryMeta.page = 1;
                vm.summaryMeta.search = "";
                vm.fetchSummaryList();
            },
            openDetailSummaryList(listType) {
                this.summaryScope = "detail";
                this.summaryListType = listType;
                this.summaryMeta.page = 1;
                this.summaryMeta.search = "";
                this.fetchSummaryList();
            },
            isOverallSummarySelected(listType) {
                if (this.summaryScope == "overall") {
                    if (this.summaryListType == listType) {
                        return true;
                    }
                }
                return false;
            },
            isDetailSummarySelected(listType) {
                if (this.summaryScope == "detail") {
                    if (this.summaryListType == listType) {
                        return true;
                    }
                }
                return false;
            },
            fetchSummaryList() {
                let vm = this;

                if (vm.summaryScope == "detail") {
                    if (!vm.sapMeta.plant_id) {
                        return;
                    }
                    if (!vm.sapMeta.planner_group) {
                        return;
                    }
                }
                if (!vm.sapMeta.from_date) {
                    vm.$store.dispatch("error", "Please select date range first.");
                    return;
                }
                if (!vm.sapMeta.to_date) {
                    vm.$store.dispatch("error", "Please select date range first.");
                    return;
                }

                let uri = "";
                let title = "";
                if (vm.summaryListType == "received") {
                    uri = "getSAPOrderReceived";
                    title = "Received Orders";
                } else if (vm.summaryListType == "completed") {
                    uri = "getSAPOrderCompleted";
                    title = "Completed Orders";
                } else if (vm.summaryListType == "deviation") {
                    uri = "getSAPOrderDeviations";
                    title = "SAP Order Deviations";
                } else if (vm.summaryListType == "pending") {
                    uri = "getSAPOrderLivePending";
                    title = "Live Pending Orders";
                } else {
                    return;
                }
                vm.summaryTitle = title;

                let loader = vm.$loading.show();
                const requestData = {
                    from_date: vm.sapMeta.from_date,
                    to_date: vm.sapMeta.to_date,
                    page: vm.summaryMeta.page,
                    per_page: vm.summaryMeta.per_page,
                    user_id: vm.sapMeta.user_id,
                    search: vm.summaryMeta.search,
                };

                if (vm.summaryScope == "detail") {
                    if (vm.sapMeta.plant_id) {
                        requestData.plant_id = vm.sapMeta.plant_id;
                    }
                    if (vm.sapMeta.department_id !== "" && vm.sapMeta.department_id !== null && vm.sapMeta.department_id !== undefined) {
                        requestData.department_id = vm.sapMeta.department_id;
                    }
                    if (vm.sapMeta.planner_group) {
                        requestData.planner_group = vm.sapMeta.planner_group;
                    }
                }

                vm.$store
                    .dispatch("post", { uri: uri, data: requestData })
                    .then(function (response) {
                        loader.hide();
                        if (response.data.data) {
                            vm.summaryRows = response.data.data;
                        } else {
                            vm.summaryRows = [];
                        }

                        if (response.data.meta) {
                            if (response.data.meta.total !== undefined && response.data.meta.total !== null) {
                                vm.summaryMeta.total = response.data.meta.total;
                            } else {
                                vm.summaryMeta.total = 0;
                            }
                            if (response.data.meta.last_page !== undefined && response.data.meta.last_page !== null) {
                                vm.summaryMeta.lastPage = response.data.meta.last_page;
                            } else {
                                vm.summaryMeta.lastPage = 1;
                            }
                            if (response.data.meta.from !== undefined && response.data.meta.from !== null) {
                                vm.summaryMeta.from = response.data.meta.from;
                            } else {
                                vm.summaryMeta.from = 1;
                            }
                            if (vm.summaryMeta.lastPage >= 3) {
                                vm.summaryMeta.maxPage = 3;
                            } else {
                                vm.summaryMeta.maxPage = vm.summaryMeta.lastPage;
                            }
                        } else {
                            vm.summaryMeta.total = 0;
                            vm.summaryMeta.lastPage = 1;
                            vm.summaryMeta.from = 1;
                            vm.summaryMeta.maxPage = 1;
                        }
                    })
                    .catch(function (error) {
                        loader.hide();
                        if (error.response && error.response.data && error.response.data.errors) {
                            vm.errors = error.response.data.errors;
                        } else {
                            vm.errors = {};
                        }
                        let message = "An error occurred while fetching SAP order data.";
                        if (error.response && error.response.data && error.response.data.message) {
                            message = error.response.data.message;
                        }
                        vm.$store.dispatch("error", message);
                    });
            },
            onSummaryPageChange(page) {
                this.summaryMeta.page = page;
                this.fetchSummaryList();
            },
            onSummaryPerPageChange() {
                this.summaryMeta.page = 1;
                this.fetchSummaryList();
            },
            searchSummaryList() {
                this.summaryMeta.page = 1;
                this.fetchSummaryList();
            },
            downloadSummaryExcel() {
                let vm = this;
                let token = "";
                if (vm.$store.getters.user && vm.$store.getters.user.token) {
                    token = vm.$store.getters.user.token;
                }

                if (!token) {
                    vm.$store.dispatch("error", "You must be logged in to download data.");
                    return;
                }

                if (!vm.summaryListType) {
                    vm.$store.dispatch("error", "Please open a list first.");
                    return;
                }

                if (!vm.sapMeta.from_date) {
                    vm.$store.dispatch("error", "Please select date range first.");
                    return;
                }
                if (!vm.sapMeta.to_date) {
                    vm.$store.dispatch("error", "Please select date range first.");
                    return;
                }

                if (vm.summaryScope == "detail") {
                    if (!vm.sapMeta.plant_id) {
                        return;
                    }
                    if (vm.sapMeta.department_id === "" || vm.sapMeta.department_id === null || vm.sapMeta.department_id === undefined) {
                        return;
                    }
                    if (!vm.sapMeta.planner_group) {
                        return;
                    }
                }

                let searchValue = "";
                if (vm.summaryMeta.search) {
                    searchValue = vm.summaryMeta.search;
                }

                let params =
                    "list_type=" + encodeURIComponent(vm.summaryListType) +
                    "&user_id=" + encodeURIComponent(vm.sapMeta.user_id) +
                    "&from_date=" + encodeURIComponent(vm.sapMeta.from_date) +
                    "&to_date=" + encodeURIComponent(vm.sapMeta.to_date) +
                    "&search=" + encodeURIComponent(searchValue);

                if (vm.summaryScope == "detail") {
                    if (vm.sapMeta.plant_id) {
                        params = params + "&plant_id=" + encodeURIComponent(vm.sapMeta.plant_id);
                    }
                    if (vm.sapMeta.department_id !== "" && vm.sapMeta.department_id !== null && vm.sapMeta.department_id !== undefined) {
                        params = params + "&department_id=" + encodeURIComponent(vm.sapMeta.department_id);
                    }
                    if (vm.sapMeta.planner_group) {
                        params = params + "&planner_group=" + encodeURIComponent(vm.sapMeta.planner_group);
                    }
                }

                let filePrefix = "sap_orders";
                if (vm.summaryListType == "received") {
                    filePrefix = "sap_received_orders";
                } else if (vm.summaryListType == "completed") {
                    filePrefix = "sap_completed_orders";
                } else if (vm.summaryListType == "deviation") {
                    filePrefix = "sap_order_deviations";
                } else if (vm.summaryListType == "pending") {
                    filePrefix = "sap_live_pending_orders";
                }

                let loader = vm.$loading.show();
                axios
                    .get(vm.$store.getters.apiUrl + "downloadSAPDashboardOrdersExcel?" + params, {
                        headers: { Authorization: "Bearer " + token },
                        responseType: "blob",
                        timeout: 0,
                    })
                    .then(function (response) {
                        let contentType = "";
                        if (response.headers && response.headers["content-type"]) {
                            contentType = response.headers["content-type"];
                        }

                        // If server returned JSON error as blob, show the message.
                        if (contentType.indexOf("application/json") !== -1) {
                            let reader = new FileReader();
                            reader.onload = function () {
                                let msg = "Could not download SAP orders.";
                                try {
                                    let json = JSON.parse(reader.result);
                                    if (json && json.message) {
                                        msg = json.message;
                                    }
                                } catch (e) {}
                                vm.$store.dispatch("error", msg);
                            };
                            reader.readAsText(response.data);
                            return;
                        }

                        let blob = new Blob([response.data], {
                            type: "text/csv",
                        });
                        let dlUrl = window.URL.createObjectURL(blob);
                        let link = document.createElement("a");
                        link.href = dlUrl;
                        link.setAttribute(
                            "download",
                            filePrefix + "_" + new Date().toISOString().slice(0, 10) + ".csv"
                        );
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(dlUrl);
                        vm.$store.dispatch("success", "SAP orders downloaded.");
                    })
                    .catch(function (error) {
                        let msg = "Could not download SAP orders.";
                        if (error.response && error.response.data) {
                            if (typeof Blob !== "undefined" && error.response.data instanceof Blob) {
                                let reader = new FileReader();
                                reader.onload = function () {
                                    try {
                                        let json = JSON.parse(reader.result);
                                        if (json && json.message) {
                                            msg = json.message;
                                        }
                                    } catch (e) {}
                                    vm.$store.dispatch("error", msg);
                                };
                                reader.readAsText(error.response.data);
                                return;
                            } else if (error.response.data.message) {
                                msg = error.response.data.message;
                            }
                        }
                        vm.$store.dispatch("error", msg);
                    })
                    .finally(function () {
                        loader.hide();
                    });
            },
            formatDateTime(date) {
                if (date) {
                    return moment(date).format("DD-MM-YYYY HH:mm");
                } else {
                    return "-";
                }
            },

            displayCount(value) {
                if (value === undefined || value === null || value === '') {
                    return 0;
                }
                return value;
            },
            displayValue(value) {
                if (value === undefined || value === null || value === '') {
                    return '-';
                }
                return value;
            },
            getEquipmentChecks(equipment) {
                if (!equipment) {
                    return [];
                }
                if (!equipment.order_checks) {
                    return [];
                }
                return equipment.order_checks;
            },
            getCheckUserName(check) {
                if (!check) {
                    return '';
                }
                if (check.user && check.user.name) {
                    return check.user.name;
                }
                if (check.User && check.User.name) {
                    return check.User.name;
                }
                return '';
            },
            getColorForOrders(value) {
                if (value > 0) {
                    return "#1273eb";
                } else {
                    return "#dfe2ef";
                }
            },
            getColorForLivePending(value) {
                if (value > 0) {
                    return "#e8cfc9";
                } else {
                    return "#dfe2ef";
                }
            },
            hasSapTileData(value) {
                if (value > 0) {
                    return true;
                }
                return false;
            },
            isPlantSelected(plant) {
                if (this.selectedPlantId == plant.plant_id) {
                    return true;
                }
                return false;
            },
            isDepartmentSelected(department) {
                if (this.selectedDepartmentId == department.department_id) {
                    return true;
                }
                return false;
            },
            isPlannerGroupSelected(plannerGroup) {
                if (this.selectedPlannerGroup == plannerGroup.planner_group) {
                    return true;
                }
                return false;
            },
            getSapTileStyle(value, isSelected) {
                let background = "#dfe2ef";
                if (value > 0) {
                    background = "#1273eb";
                }

                let style = {
                    background: background,
                    border: "4px solid transparent",
                };

                if (isSelected) {
                    style.border = "5px solid #ffffff";
                    style.boxShadow = "0 0 0 4px #0a58ca, 0 0 18px rgba(10, 88, 202, 0.85)";
                    style.transform = "scale(1.03)";
                }

                return style;
            },
            getSapLivePendingStyle(value, isSelected) {
                let background = "#dfe2ef";
                if (value > 0) {
                    background = "#e8cfc9";
                }

                let style = {
                    background: background,
                    border: "4px solid transparent",
                    color: "#394758",
                };

                if (isSelected) {
                    style.border = "5px solid #ffffff";
                    style.boxShadow = "0 0 0 4px #a86b5f, 0 0 18px rgba(168, 107, 95, 0.85)";
                    style.transform = "scale(1.03)";
                }

                return style;
            },
            getSapDeviationStyle(value, isSelected) {
                let background = "#dfe2ef";
                if (value > 0) {
                    background = "#f5c6cb";
                }

                let style = {
                    background: background,
                    border: "4px solid transparent",
                    color: "#394758",
                };

                if (isSelected) {
                    style.border = "5px solid #ffffff";
                    style.boxShadow = "0 0 0 4px #c82333, 0 0 18px rgba(200, 35, 51, 0.75)";
                    style.transform = "scale(1.03)";
                }

                return style;
            },
            dateFieldClass(field) {
                if (this.errors[field]) {
                    return "is-invalid";
                } else {
                    return "";
                }
            },
            errorText(field) {
                if (this.errors[field]) {
                    return this.errors[field][0];
                } else {
                    return "";
                }
            },
        },
    };
</script>

<style scoped>
    .sap-excel-download-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        height: 34px;
        padding: 0 10px;
        border: 1px solid #c3e6cb;
        border-radius: 6px;
        background: #e8f5e9;
        color: #1b7e34;
        font-size: 13px;
        font-weight: 600;
        line-height: 1;
        white-space: nowrap;
        cursor: pointer;
        transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
    }

    .sap-excel-download-btn:hover {
        background: #1b7e34;
        border-color: #1b7e34;
        color: #fff;
    }

    .sap-excel-download-btn i {
        font-size: 13px;
    }

    .sales-tile:not(.sap-tile-default) #label,
    .sales-tile:not(.sap-tile-default) h6 {
        color: #fff !important;
    }

    .sales-tile.sap-tile-default #label,
    .sales-tile.sap-tile-default h6 {
        color: #394758 !important;
    }

    .sales-tile.sap-live-pending-tile:not(.sap-tile-default) #label,
    .sales-tile.sap-live-pending-tile:not(.sap-tile-default) h6 {
        color: #394758 !important;
    }

    .sales-tile.sap-deviation-tile:not(.sap-tile-default) #label,
    .sales-tile.sap-deviation-tile:not(.sap-tile-default) h6 {
        color: #394758 !important;
    }

    .dashboard-header .dashboard-header-content .sales-tile-container .sales-tile.sap-live-pending-tile.sap-tile-selected {
        border: 5px solid #ffffff !important;
        box-shadow: 0 0 0 4px #a86b5f, 0 0 18px rgba(168, 107, 95, 0.85) !important;
        transform: scale(1.03);
        z-index: 3;
        position: relative;
    }

    .dashboard-header .dashboard-header-content .sales-tile-container .sales-tile.sap-deviation-tile.sap-tile-selected {
        border: 5px solid #ffffff !important;
        box-shadow: 0 0 0 4px #c82333, 0 0 18px rgba(200, 35, 51, 0.75) !important;
        transform: scale(1.03);
        z-index: 3;
        position: relative;
    }

    .card .card-body {
        padding: 10px;
    }

    .card .card-footer {
        padding: 10px;
    }

    .dashboard-header {
        width: 100%;
        overflow: visible;
    }

    .dashboard-header-content {
        width: 100%;
        margin: 0;
        padding: 0;
    }

    .sales-tile-container {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        width: 100%;
        margin: 0;
        padding: 8px 4px;
        overflow: visible;
    }

    .sales-tile {
        cursor: pointer;
        margin: 0;
        padding: 12px;
        border-radius: 12px;
        border: 3px solid transparent;
        box-sizing: border-box;
        text-align: center;
        transition: all 0.3s;
        min-width: 140px;
        flex: 0 0 auto;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }

    .dashboard-header .dashboard-header-content .sales-tile-container .sales-tile.sap-tile-selected {
        border: 5px solid #ffffff !important;
        box-shadow: 0 0 0 4px #0a58ca, 0 0 18px rgba(10, 88, 202, 0.85) !important;
        transform: scale(1.03);
        z-index: 3;
        position: relative;
    }

    .sap-live-pending-gap {
        width: 80px;
        min-width: 80px;
        flex: 0 0 80px;
    }

    .sales-tile.sap-live-pending-tile {
        margin-left: 0;
    }

    .sap-orders-wrapper {
        width: 100%;
    }

    .sap-orders-filter-section {
        position: sticky;
        top: 0;
        padding: 15px;
        margin: 0;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        border-bottom: 1px solid #e0e0e0;
        z-index: 100;
    }

    .sap-orders-filter-section .card {
        margin-bottom: 0;
    }

    .sap-orders-content {
        padding: 15px;
    }

    .sap-orders-content .row {
        margin-left: -15px;
        margin-right: -15px;
    }

    .sap-orders-content .row > [class*="col-"] {
        padding-left: 15px;
        padding-right: 15px;
    }

    .table {
        margin-bottom: 0rem;
    }

    .table-responsive.tabletop {
        max-height: none;
        overflow-x: auto;
    }

    .equipment-row {
        background-color: #e9ecef;
        font-weight: bold;
    }

    .check-row {
        background-color: #f8f9fa;
    }
</style>
